import { 
  createSlice, 
  createAsyncThunk, 
  createSelector, 
  PayloadAction
} from '@reduxjs/toolkit';
import { RootState } from '../index';
import { 
  SettingsState, 
  AppearanceSettings, 
  NotificationSettings, 
  PrivacySettings, 
  DisplaySettings,
  FeedPreferences,
  SyncSettings,
  AccountSettings,
  SessionSettings,
  ThemePreference,
  LanguageCode,
  MeasurementSystem,
  PrivacySetting,
  TrainingViewMode,
  FeedSortMode
} from '../../types/settings';
import settingsService from '../../services/firebase/settings.service';

// Define initial state
const initialState: SettingsState = {
  appearance: {
    theme: 'system',
    fontSize: 'medium',
    reducedMotion: false,
    reducedTransparency: false,
    highContrast: false
  },
  notifications: {
    enabled: true,
    pushNotifications: true,
    emailNotifications: true,
    trainingReminders: true,
    comments: true,
    likes: true,
    mentions: true,
    friendRequests: true,
    socialActivity: true,
    promotions: true,
    achievements: true,
    trainingSummary: true,
    weeklyReport: true,
    marketing: false,
    quiet: {
      enabled: false,
      start: "22:00",
      end: "08:00"
    }
  },
  privacy: {
    profileVisibility: 'public',
    trainingVisibility: 'friends',
    competitionVisibility: 'friends',
    statsVisibility: 'friends',
    goalsVisibility: 'friends',
    allowTagging: true,
    showOnlineStatus: true,
    showActivityStatus: true,
    dataSharing: {
      shareTrainingData: true,
      shareStats: true,
      shareLocation: true,
      allowAnonymousDataCollection: true
    }
  },
  display: {
    language: 'en',
    measurementSystem: 'imperial',
    defaultTrainingView: 'calendar',
    defaultFeedSort: 'recent',
    showWelcomeScreens: true,
    showTips: true,
    enableSoundEffects: true,
    enableHapticFeedback: true,
    dateFormat: 'MM/DD/YYYY',
    timeFormat: '12h'
  },
  feed: {
    preferredContent: ["training", "competitions", "promotions", "belts"],
    hiddenTopics: [],
    showFriendSuggestions: true,
    showTrendingPosts: true,
    autoPlayVideos: true,
    saveDataMode: false
  },
  sync: {
    syncEnabled: true,
    syncOnWifi: true,
    syncFrequency: 'realtime',
    syncMedia: true,
    backgroundSync: true,
    maxStorageSize: 500,
    offlineMode: false,
    lastSyncTime: null
  },
  account: {
    emailVerified: false,
    twoFactorEnabled: false,
    recoveryEmailSet: false,
    recoveryPhoneSet: false,
    connectedAccounts: {
      google: false,
      facebook: false,
      apple: false
    },
    healthConnections: {
      enabled: false,
      appleHealth: false,
      googleFit: false,
      strava: false,
      garmin: false,
      fitbit: false,
      withings: false,
      myFitnessPal: false,
      lastSync: {},
      permissions: {}
    },
    dataExport: {
      lastExportDate: null,
      availableExports: []
    }
  },
  session: {
    sessionTimeout: 30,
    rememberMe: true,
    lastDevices: []
  },
  isLoading: false,
  error: null,
  lastUpdated: null
};

// Async thunks
export const fetchUserSettings = createAsyncThunk(
  'settings/fetchUserSettings',
  async (userId: string, { rejectWithValue }) => {
    try {
      // Fetch settings from Firebase
      const settings = await settingsService.fetchUserSettings(userId);
      
      if (!settings) {
        // Initialize with default settings if none exist
        await settingsService.initializeUserSettings(userId, initialState);
        return {
          ...initialState,
          lastUpdated: Date.now()
        };
      }
      
      return settings as SettingsState;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

// Define a deeply partial type for settings
type DeepPartialSettings = {
  appearance?: Partial<AppearanceSettings>;
  notifications?: Partial<NotificationSettings>;
  privacy?: Partial<PrivacySettings>;
  display?: Partial<DisplaySettings>;
  feed?: Partial<FeedPreferences>;
  sync?: Partial<SyncSettings>;
  account?: Partial<AccountSettings>;
  session?: Partial<SessionSettings>;
};

export const updateUserSettings = createAsyncThunk(
  'settings/updateUserSettings',
  async (
    {
      userId,
      settings
    }: {
      userId: string;
      settings: DeepPartialSettings;
    },
    { rejectWithValue }
  ) => {
    try {
      // Update settings in Firebase
      await settingsService.updateUserSettings(userId, settings);

      return {
        ...settings,
        lastUpdated: Date.now()
      };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

// Specific update thunks for different settings sections
export const updateAppearanceSettings = createAsyncThunk(
  'settings/updateAppearanceSettings',
  async (
    {
      userId,
      appearance
    }: {
      userId: string;
      appearance: Partial<AppearanceSettings>;
    },
    { rejectWithValue }
  ) => {
    try {
      // Update appearance settings in Firebase
      await settingsService.updateSettingsSection(userId, 'appearance', appearance);
      
      return {
        appearance,
        lastUpdated: Date.now()
      };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

export const updateNotificationSettings = createAsyncThunk(
  'settings/updateNotificationSettings',
  async (
    {
      userId,
      notifications
    }: {
      userId: string;
      notifications: Partial<NotificationSettings>;
    },
    { rejectWithValue }
  ) => {
    try {
      // Update notification settings in Firebase
      await settingsService.updateSettingsSection(userId, 'notifications', notifications);
      
      return {
        notifications,
        lastUpdated: Date.now()
      };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

export const updatePrivacySettings = createAsyncThunk(
  'settings/updatePrivacySettings',
  async (
    {
      userId,
      privacy
    }: {
      userId: string;
      privacy: Partial<PrivacySettings>;
    },
    { rejectWithValue }
  ) => {
    try {
      // Update privacy settings in Firebase
      await settingsService.updateSettingsSection(userId, 'privacy', privacy);
      
      return {
        privacy,
        lastUpdated: Date.now()
      };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

export const updateDisplaySettings = createAsyncThunk(
  'settings/updateDisplaySettings',
  async (
    {
      userId,
      display
    }: {
      userId: string;
      display: Partial<DisplaySettings>;
    },
    { rejectWithValue }
  ) => {
    try {
      // Update display settings in Firebase
      await settingsService.updateSettingsSection(userId, 'display', display);
      
      return {
        display,
        lastUpdated: Date.now()
      };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

// Create the settings slice
const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    resetSettingsError: (state) => {
      state.error = null;
    },
    
    // Appearance settings actions
    setTheme: (state, action: PayloadAction<ThemePreference>) => {
      state.appearance.theme = action.payload;
      state.lastUpdated = Date.now();
    },
    
    setFontSize: (state, action: PayloadAction<'small' | 'medium' | 'large'>) => {
      state.appearance.fontSize = action.payload;
      state.lastUpdated = Date.now();
    },
    
    toggleReducedMotion: (state) => {
      state.appearance.reducedMotion = !state.appearance.reducedMotion;
      state.lastUpdated = Date.now();
    },
    
    toggleHighContrast: (state) => {
      state.appearance.highContrast = !state.appearance.highContrast;
      state.lastUpdated = Date.now();
    },
    
    // Display settings actions
    setLanguage: (state, action: PayloadAction<LanguageCode>) => {
      state.display.language = action.payload;
      state.lastUpdated = Date.now();
    },
    
    setMeasurementSystem: (state, action: PayloadAction<MeasurementSystem>) => {
      state.display.measurementSystem = action.payload;
      state.lastUpdated = Date.now();
    },
    
    setDefaultTrainingView: (state, action: PayloadAction<TrainingViewMode>) => {
      state.display.defaultTrainingView = action.payload;
      state.lastUpdated = Date.now();
    },
    
    setDefaultFeedSort: (state, action: PayloadAction<FeedSortMode>) => {
      state.display.defaultFeedSort = action.payload;
      state.lastUpdated = Date.now();
    },
    
    // Notification settings actions
    toggleNotifications: (state, action: PayloadAction<boolean>) => {
      state.notifications.enabled = action.payload;
      state.lastUpdated = Date.now();
    },
    
    togglePushNotifications: (state, action: PayloadAction<boolean>) => {
      state.notifications.pushNotifications = action.payload;
      state.lastUpdated = Date.now();
    },
    
    toggleEmailNotifications: (state, action: PayloadAction<boolean>) => {
      state.notifications.emailNotifications = action.payload;
      state.lastUpdated = Date.now();
    },
    
    setQuietHours: (state, action: PayloadAction<{enabled: boolean, start?: string, end?: string}>) => {
      state.notifications.quiet.enabled = action.payload.enabled;
      if (action.payload.start) {
        state.notifications.quiet.start = action.payload.start;
      }
      if (action.payload.end) {
        state.notifications.quiet.end = action.payload.end;
      }
      state.lastUpdated = Date.now();
    },
    
    // Privacy settings actions
    setPrivacySetting: (state, action: PayloadAction<{
      setting: keyof Pick<PrivacySettings, 
        'profileVisibility' | 
        'trainingVisibility' | 
        'competitionVisibility' | 
        'statsVisibility' | 
        'goalsVisibility'>,
      value: PrivacySetting
    }>) => {
      const { setting, value } = action.payload;
      (state.privacy[setting] as PrivacySetting) = value;
      state.lastUpdated = Date.now();
    },
    
    toggleAllowTagging: (state, action: PayloadAction<boolean>) => {
      state.privacy.allowTagging = action.payload;
      state.lastUpdated = Date.now();
    },
    
    toggleShowOnlineStatus: (state, action: PayloadAction<boolean>) => {
      state.privacy.showOnlineStatus = action.payload;
      state.lastUpdated = Date.now();
    },
    
    // Feed settings actions
    addPreferredContent: (state, action: PayloadAction<string>) => {
      if (!state.feed.preferredContent.includes(action.payload)) {
        state.feed.preferredContent.push(action.payload);
      }
      state.lastUpdated = Date.now();
    },
    
    removePreferredContent: (state, action: PayloadAction<string>) => {
      state.feed.preferredContent = state.feed.preferredContent.filter(
        content => content !== action.payload
      );
      state.lastUpdated = Date.now();
    },
    
    addHiddenTopic: (state, action: PayloadAction<string>) => {
      if (!state.feed.hiddenTopics.includes(action.payload)) {
        state.feed.hiddenTopics.push(action.payload);
      }
      state.lastUpdated = Date.now();
    },
    
    removeHiddenTopic: (state, action: PayloadAction<string>) => {
      state.feed.hiddenTopics = state.feed.hiddenTopics.filter(
        topic => topic !== action.payload
      );
      state.lastUpdated = Date.now();
    },
    
    toggleSaveDataMode: (state, action: PayloadAction<boolean>) => {
      state.feed.saveDataMode = action.payload;
      
      // If save data mode is enabled, also disable autoplay videos
      if (action.payload) {
        state.feed.autoPlayVideos = false;
      }
      
      state.lastUpdated = Date.now();
    },
    
    // Sync settings actions
    toggleSyncEnabled: (state, action: PayloadAction<boolean>) => {
      state.sync.syncEnabled = action.payload;
      state.lastUpdated = Date.now();
    },
    
    setSyncFrequency: (state, action: PayloadAction<'realtime' | 'hourly' | 'daily' | 'manual'>) => {
      state.sync.syncFrequency = action.payload;
      state.lastUpdated = Date.now();
    },
    
    toggleOfflineMode: (state, action: PayloadAction<boolean>) => {
      state.sync.offlineMode = action.payload;
      state.lastUpdated = Date.now();
    },
    
    setLastSyncTime: (state, action: PayloadAction<number>) => {
      state.sync.lastSyncTime = action.payload;
      state.lastUpdated = Date.now();
    },
    
    // Session settings
    setSessionTimeout: (state, action: PayloadAction<number>) => {
      state.session.sessionTimeout = action.payload;
      state.lastUpdated = Date.now();
    },
    
    toggleRememberMe: (state, action: PayloadAction<boolean>) => {
      state.session.rememberMe = action.payload;
      state.lastUpdated = Date.now();
    },
    
    // Reset to defaults
    resetToDefaults: (state) => {
      return { 
        ...initialState,
        lastUpdated: Date.now()
      };
    }
  },
  extraReducers: (builder) => {
    builder
      // fetchUserSettings
      .addCase(fetchUserSettings.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUserSettings.fulfilled, (state, action) => {
        return {
          ...state,
          ...action.payload,
          isLoading: false,
          error: null
        };
      })
      .addCase(fetchUserSettings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // updateUserSettings
      .addCase(updateUserSettings.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateUserSettings.fulfilled, (state, action) => {
        const { lastUpdated, ...settings } = action.payload;

        // Deep merge the settings
        if (settings.appearance) {
          state.appearance = {
            ...state.appearance,
            ...settings.appearance
          };
        }

        if (settings.notifications) {
          state.notifications = {
            ...state.notifications,
            ...settings.notifications
          };
        }

        if (settings.privacy) {
          state.privacy = {
            ...state.privacy,
            ...settings.privacy
          };
        }

        if (settings.display) {
          state.display = {
            ...state.display,
            ...settings.display
          };
        }

        if (settings.feed) {
          state.feed = {
            ...state.feed,
            ...settings.feed
          };
        }

        if (settings.sync) {
          state.sync = {
            ...state.sync,
            ...settings.sync
          };
        }

        if (settings.account) {
          state.account = {
            ...state.account,
            ...settings.account
          };
        }

        if (settings.session) {
          state.session = {
            ...state.session,
            ...settings.session
          };
        }

        state.lastUpdated = lastUpdated;
        state.isLoading = false;
        state.error = null;
      })
      .addCase(updateUserSettings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // updateAppearanceSettings
      .addCase(updateAppearanceSettings.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateAppearanceSettings.fulfilled, (state, action) => {
        if (action.payload.appearance) {
          state.appearance = {
            ...state.appearance,
            ...action.payload.appearance
          };
        }
        state.lastUpdated = action.payload.lastUpdated;
        state.isLoading = false;
      })
      .addCase(updateAppearanceSettings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // updateNotificationSettings
      .addCase(updateNotificationSettings.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateNotificationSettings.fulfilled, (state, action) => {
        if (action.payload.notifications) {
          state.notifications = {
            ...state.notifications,
            ...action.payload.notifications
          };
        }
        state.lastUpdated = action.payload.lastUpdated;
        state.isLoading = false;
      })
      .addCase(updateNotificationSettings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // updatePrivacySettings
      .addCase(updatePrivacySettings.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updatePrivacySettings.fulfilled, (state, action) => {
        if (action.payload.privacy) {
          state.privacy = {
            ...state.privacy,
            ...action.payload.privacy
          };
        }
        state.lastUpdated = action.payload.lastUpdated;
        state.isLoading = false;
      })
      .addCase(updatePrivacySettings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // updateDisplaySettings
      .addCase(updateDisplaySettings.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateDisplaySettings.fulfilled, (state, action) => {
        if (action.payload.display) {
          state.display = {
            ...state.display,
            ...action.payload.display
          };
        }
        state.lastUpdated = action.payload.lastUpdated;
        state.isLoading = false;
      })
      .addCase(updateDisplaySettings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  }
});

// Export actions
export const {
  resetSettingsError,
  setTheme,
  setFontSize,
  toggleReducedMotion,
  toggleHighContrast,
  setLanguage,
  setMeasurementSystem,
  setDefaultTrainingView,
  setDefaultFeedSort,
  toggleNotifications,
  togglePushNotifications,
  toggleEmailNotifications,
  setQuietHours,
  setPrivacySetting,
  toggleAllowTagging,
  toggleShowOnlineStatus,
  addPreferredContent,
  removePreferredContent,
  addHiddenTopic,
  removeHiddenTopic,
  toggleSaveDataMode,
  toggleSyncEnabled,
  setSyncFrequency,
  toggleOfflineMode,
  setLastSyncTime,
  setSessionTimeout,
  toggleRememberMe,
  resetToDefaults
} = settingsSlice.actions;

// Basic selectors
export const selectSettingsState = (state: RootState) => state.settings;

export const selectAppearance = createSelector(
  selectSettingsState,
  (state) => state.appearance
);

export const selectNotifications = createSelector(
  selectSettingsState,
  (state) => state.notifications
);

export const selectPrivacy = createSelector(
  selectSettingsState,
  (state) => state.privacy
);

export const selectDisplay = createSelector(
  selectSettingsState,
  (state) => state.display
);

export const selectFeedPreferences = createSelector(
  selectSettingsState,
  (state) => state.feed
);

export const selectSync = createSelector(
  selectSettingsState,
  (state) => state.sync
);

export const selectAccount = createSelector(
  selectSettingsState,
  (state) => state.account
);

export const selectSession = createSelector(
  selectSettingsState,
  (state) => state.session
);

export const selectSettingsLoading = createSelector(
  selectSettingsState,
  (state) => state.isLoading
);

export const selectSettingsError = createSelector(
  selectSettingsState,
  (state) => state.error
);

export const selectLastUpdated = createSelector(
  selectSettingsState,
  (state) => state.lastUpdated
);

// Specific selectors
export const selectTheme = createSelector(
  selectAppearance,
  (appearance) => appearance.theme
);

export const selectLanguage = createSelector(
  selectDisplay,
  (display) => display.language
);

export const selectMeasurementSystem = createSelector(
  selectDisplay,
  (display) => display.measurementSystem
);

export const selectQuietHours = createSelector(
  selectNotifications,
  (notifications) => notifications.quiet
);

export const selectOfflineMode = createSelector(
  selectSync,
  (sync) => sync.offlineMode
);

export const selectPrivacyVisibility = createSelector(
  selectPrivacy,
  (privacy) => ({
    profile: privacy.profileVisibility,
    training: privacy.trainingVisibility,
    competition: privacy.competitionVisibility,
    stats: privacy.statsVisibility,
    goals: privacy.goalsVisibility
  })
);

export const selectDataSharing = createSelector(
  selectPrivacy,
  (privacy) => privacy.dataSharing
);

export default settingsSlice.reducer;